/**
 * Templates ZPL para impressora Zebra ZD220
 * 203 DPI | 4 polegadas (102mm) | Velocidade: 4 pol/s
 * ZPL Reference: https://www.zebra.com/content/dam/zebra/manuals/printers/common/programming/zpl-zbi2-pm-en.pdf
 */

import QRCode from 'qrcode';
import JsBarcode from 'jsbarcode';

export interface DadosEtiquetaZpl {
  empresa: string;      // "AW Administradora (LM TRANSPORTES)"
  placa: string;        // "TBS5J51"
  data: string;         // "27/11/2025"
  marcaModelo: string;  // "CHEV/ONIX 10MT HB *M.M. 104735*"
  cor: string;          // "PINT EXT(GAZ) SOL *BRANCO SUMMIT*"
  localLacre: string;   // "PDI TEGMA"
  chassi: string;       // "9BGEA48A0SG295606"
}

/**
 * Etiqueta de processo — 4" x 3.5" (102mm x 89mm) @ 203 DPI
 *
 * Layout:
 *   AW Administradora (EMPRESA)          <- bold, centralizado
 *   Placa: TBS5J51 - Data: 27/11/2025    <- centralizado
 *   MARCA/MODELO - COR                   <- centralizado (2 linhas se necessário)
 *   Local Lacre: PDI TEGMA               <- centralizado
 *   9BGEA48A0SG295606                    <- centralizado
 *   ||||||||||||||||||||||||||||||||      <- barcode Code128 (chassi)
 *   Quadra: ____________  Rua ______     <- campos manuais
 */
export async function gerarHtmlEtiqueta(dados: DadosEtiquetaZpl): Promise<string> {
  const { empresa, placa, data, marcaModelo, cor, localLacre, chassi } = dados;

  const qrDataUrl = await QRCode.toDataURL(chassi, { width: 96, margin: 1 });

  const svgEl = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  JsBarcode(svgEl, chassi, {
    format: "code128",
    displayValue: false,
    width: 2,
    height: 60,
    margin: 0,
    background: "transparent",
  });
  const barcodeSvg = new XMLSerializer().serializeToString(svgEl);

  return `
    <html><head><title>Etiqueta - ${placa}</title>
    <style>
      @page { size: 102mm 89mm; margin: 2mm 3mm; }
      * { box-sizing: border-box; }
      body { font-family: Arial, sans-serif; font-size: 10px; text-align: center; margin: 0; padding: 0; width: 96mm; }
      .empresa { font-size: 12px; font-weight: bold; margin-bottom: 2px; }
      .linha { margin: 2px 0; font-size: 10px; }
      .chassi-texto { font-size: 11px; font-weight: bold; margin: 2px 0; }
      .barcode { width: 100%; margin: 3px 0; }
      .barcode svg { width: 100%; height: 60px; }
      .campos { text-align: left; margin: 3px 0; font-size: 9px; border-top: 1px solid #000; padding-top: 3px; }
      .qr-row { display: flex; align-items: center; justify-content: center; margin: 2px 0; }
      .qr-row img { width: 72px; height: 72px; }
    </style>
    </head><body>
    <div class="empresa">${empresa}</div>
    <div class="linha">Placa: <strong>${placa}</strong> — Data: ${data}</div>
    <div class="linha">${marcaModelo} — ${cor}</div>
    <div class="linha" style="word-break:break-word;white-space:normal;">Local Lacre: ${localLacre}</div>
    <div class="chassi-texto">${chassi}</div>
    <div class="barcode">${barcodeSvg}</div>
    <div class="campos">Quadra: ____________________&nbsp;&nbsp;Rua: ____________________</div>
    <div class="qr-row">
      <img src="${qrDataUrl}" alt="QR ${chassi}" />
    </div>
    <script>window.onload = function() { window.print(); window.close(); };</script>
    </body></html>
  `;
}

function norm(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')  // remove acentos
    .replace(/[^\x00-\x7F]/g, '?');   // substitui caracteres fora do ASCII por ?
}

export function gerarZplEtiquetaProcesso(dados: DadosEtiquetaZpl): string {
  const {
    empresa,
    placa,
    data,
    marcaModelo,
    cor,
    localLacre,
    chassi,
  } = dados;

  const linhaMarcaCor = `${norm(marcaModelo)} - ${norm(cor)}`;
  const linhaPlacaData = `Placa: ${norm(placa)} - Data: ${norm(data)}`;

  return [
    "^XA",
    "^PW812",          // largura 4 polegadas (203 DPI)
    "^LL711",          // altura 3.5 polegadas (203 DPI)
    "^CI0",            // IBM Code Page 850 (ASCII seguro)
    "^MMT",            // modo tear
    "^LH20,20",        // margem: desloca home 20 dots (~2.5mm) em X e Y

    // Empresa - negrito, centralizado
    `^FO20,15^A0N,38,32^FB732,1,0,C,0^FD${norm(empresa)}^FS`,

    // Placa - Data
    `^FO20,62^A0N,30,26^FB732,1,0,C,0^FD${linhaPlacaData}^FS`,

    // Marca/Modelo - Cor (bloco de 2 linhas)
    `^FO20,100^A0N,28,24^FB732,2,0,C,0^FD${linhaMarcaCor}^FS`,

    // Local Lacre — até 5 linhas, fonte menor para textos longos (endereços)
    `^FO20,165^A0N,22,18^FB732,5,0,C,0^FDLocal Lacre: ${norm(localLacre)}^FS`,

    // Chassi (deslocado para abrir espaço das 5 linhas do Local Lacre: 5×22=110)
    `^FO20,285^A0N,30,26^FB732,1,0,C,0^FD${norm(chassi)}^FS`,

    // Barcode Code128 do chassi — largura total
    `^FO10,325^BY2,2,70^BCN,70,Y,N,N^FD${norm(chassi)}^FS`,

    // Quadra / Rua — campos manuais
    `^FO20,415^A0N,26,22^FDQuadra: __________________   Rua __________________^FS`,

    // QR Code — centralizado na parte inferior, mesmo valor do barcode (chassi)
    `^FO255,450^BQN,2,6,M^FDMA,${norm(chassi)}^FS`,

    "^XZ",
  ].join("");
}
